import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Review : famille "évaluation exprimée par un tiers".
 *
 * Appréciation laissée par un tiers après une prestation, une vente, un séjour
 * ou un événement, éventuellement publiable. Construite sur le modèle "famille
 * + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète (avis sur une réservation,
 * sur une commande, sur un événement, sur un ticket de support) : il connaît la
 * famille générique `review`. Le vocabulaire métier est porté par le champ
 * `type` (chaîne libre déclarée par l'app, ex. "booking", "order", "event").
 *
 * Le cœur de la famille est la note `rating` (l'évaluation elle-même), donc
 * requise. Références vers d'autres familles = de simples ids (aucun import de
 * schéma) : `authorContactId` est l'auteur (le tiers), optionnel car un avis
 * peut être anonyme ou importé d'une plateforme externe. La cible évaluée
 * (targetRef) est polymorphe : `bookingId`, `orderId`, `eventId` ou `ticketId`,
 * donc chaque cible reste optionnelle. `replyMessageId` porte la réponse
 * publiée à l'avis (référence `message`).
 *
 * Sécurité : rien de sensible signalé, la famille reste org-wide en v0 (même
 * doctrine qu'asset).
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille review (hors champs standard du socle). */
export const reviewCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "booking", "order", "event", "ticket"). */
  type: z.string().min(1),
  /** Note attribuée : cœur de l'évaluation. Échelle libre pilotée par l'app (ex. 1 à 5). */
  rating: z.number(),
  /** Fiche `contact` de l'auteur (le tiers). Optionnel (avis anonyme ou importé d'une plateforme externe). */
  authorContactId: z.string().min(1).optional(),
  /** Commentaire libre accompagnant la note. Optionnel. */
  comment: z.string().min(1).optional(),
  /** Cible évaluée (targetRef) : réservation notée (référence `booking`). Optionnel. */
  bookingId: z.string().min(1).optional(),
  /** Cible évaluée (targetRef) : commande notée (référence `order`). Optionnel. */
  orderId: z.string().min(1).optional(),
  /** Cible évaluée (targetRef) : événement noté (référence `event`). Optionnel. */
  eventId: z.string().min(1).optional(),
  /** Cible évaluée (targetRef) : prestation ou ticket noté (référence `ticket`). Optionnel. */
  ticketId: z.string().min(1).optional(),
  /** Date de publication de l'avis (renseignée seulement si publié). Optionnel. */
  publishedAt: z.date().optional(),
  /** Réponse publiée à l'avis (référence `message`). Optionnel. */
  replyMessageId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille review (champs standard + champs métier). */
export const ReviewSchema = z.object({
  ...baseEntityFields,
  ...reviewCoreFields,
});

/** Type d'un review. */
export type Review = z.infer<typeof ReviewSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const ReviewCreateInputSchema = z.object({
  ...reviewCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un review. */
export type ReviewCreateInput = z.infer<typeof ReviewCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const ReviewUpdateInputSchema = z
  .object({ ...reviewCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'un review. */
export type ReviewUpdateInput = z.infer<typeof ReviewUpdateInputSchema>;
